// App.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ExercisesList from './components/ExercisesList';
import ExerciseDetail from './components/ExerciseDetail';
import Login from './components/Login';
import SignUp from './components/SignUp';
import AdminPanel from './components/admin/AdminPanel';
import LeaderboardPage from './pages/LeaderboardPage';
import AdminRoute from './components/admin/AdminRoute';
import TopPanel from './components/TopPanel';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AdminProvider } from './contexts/AdminContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';

function AppContent() {
  const { user, loading, signOut } = useAuth();
  const { theme, toggleTheme, isLoading: themeLoading } = useTheme();
  const location = useLocation();

  // Derive darkMode for compatibility with existing components
  const darkMode = theme === 'dark';

  // Stable logout handler to prevent unnecessary re-renders
  const handleLogout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  // Memoized check for public pages
  const isPublicPage = useMemo(() => {
    return location.pathname === '/login' || location.pathname === '/signup';
  }, [location.pathname]);

  // Memoized TopPanel props for optimal performance
  const topPanelProps = useMemo(() => ({
    user,
    onLogout: user ? handleLogout : null,
    showNavigation: user && !isPublicPage,
    showUserInfo: user && !isPublicPage
  }), [user, handleLogout, isPublicPage]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="App themed-background" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* TopPanel is always rendered - no conditional logic */}
      <TopPanel {...topPanelProps} />

      <div className="main-content" style={{ flex: 1, paddingTop: '40px' }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route
            path="/"
            element={user ? <ExercisesList userId={user.id} /> : <Navigate to="/login" />}
          />
          <Route
            path="/exercise/:id"
            element={user ? <ExerciseDetail darkMode={darkMode} /> : <Navigate to="/login" />}
          />
          <Route
            path="/leaderboard"
            element={<LeaderboardPage />}
          />
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            }
          />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <AdminProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AppContent />
            </Router>
          </AdminProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
